import { useMemo, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import { AuthGate } from './components/layout/AuthGate'
import { AppShell } from './components/layout/AppShell'
import { RoutePlaceholder } from './components/layout/RoutePlaceholder'
import { AuthSessionProvider, getFirebaseIdToken, useAuthSession } from './lib/firebase'
import { HomeLibraryPage, StationLearnerPage } from './features/study'
import { ReviewRoute } from './features/review'
import { ExamRoute } from './features/exam'
import { AdminCMSPage, AdminStationToolbar, createAdminApi } from './features/admin'
import { AdminUsersPage } from './features/admin/AdminUsersPage'

function AdminOnly({ children }: { children: React.ReactNode }) {
  const session = useAuthSession()
  if (!session.isAdmin) {
    return (
      <RoutePlaceholder
        eyebrow="صلاحيات المسؤول"
        title="هذه الصفحة متاحة للمسؤولين فقط"
        description="سجّل الدخول بحساب مسؤول لعرض أدوات إدارة المحتوى."
        icon={ShieldAlert}
      />
    )
  }
  return children
}

function StationRoute() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const session = useAuthSession()
  const [busy, setBusy] = useState(false)
  const api = useMemo(() => createAdminApi({ getToken: getFirebaseIdToken }), [])

  async function archiveStation() {
    if (!id || busy) return
    if (!window.confirm('أرشفة هذه المحطة؟ يمكن استعادتها لاحقًا من لوحة التحكم.')) return
    setBusy(true)
    try {
      await api.archiveStation(id)
      navigate('/')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {session.isAdmin && (
        <AdminStationToolbar
          stationId={id}
          disabled={busy}
          onEdit={() => navigate(`/admin?station=${encodeURIComponent(id)}`)}
          onPreview={() => navigate(`/admin?station=${encodeURIComponent(id)}`)}
          onAddSubquestion={() => navigate(`/admin?station=${encodeURIComponent(id)}&addQuestion=1`)}
          onArchive={() => void archiveStation()}
          previewing
        />
      )}
      <StationLearnerPage />
    </>
  )
}

function AppRoutes() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomeLibraryPage />} />
        <Route path="/stations/:id" element={<StationRoute />} />
        <Route path="/cases/:id" element={<StationRoute />} />
        <Route path="/review" element={<ReviewRoute />} />
        <Route path="/exam" element={<ExamRoute />} />
        <Route path="/admin" element={<AdminOnly><AdminCMSPage getAuthToken={getFirebaseIdToken} /></AdminOnly>} />
        <Route path="/admin/users" element={<AdminOnly><AdminUsersPage /></AdminOnly>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  )
}

export default function App() {
  return (
    <AuthSessionProvider>
      <BrowserRouter>
        <AuthGate>
          <AppRoutes />
        </AuthGate>
      </BrowserRouter>
    </AuthSessionProvider>
  )
}
